import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Loader2, CheckCircle2, AlertCircle, Sparkles, Lock, Trash2, ShieldCheck } from "lucide-react";

/**
 * Phase 4 — Onboard Plant.
 *   1. Admin picks a SolarOS station first.
 *   2. Form pre-populates Name (from station.name) and Location (from address).
 *   3. Plant Size is locked to SolarOS's installedCapacity (read-only).
 *   4. Commissioning date drives a 5-year warranty preview.
 *   5. Deterministic RPPL Plant ID: `legalname_size_location_YYYYMMDD`.
 *   6. Registry table below lists onboarded plants — Super Admin can delete.
 */
export default function CreatePlant() {
    const { user } = useAuth();
    const isSuperAdmin = user?.role === "super_admin";

    const [form, setForm] = useState({
        solaros_station_id: "",
        customer_name: "",
        customer_email: "",
        customer_phone: "",
        size_kw: "",
        location: "",
        commissioning_date: new Date().toISOString().slice(0, 10),
    });
    const [stations, setStations] = useState([]);
    const [stationMeta, setStationMeta] = useState({ available: 0, total: 0, onboarded: 0 });
    const [registry, setRegistry] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    const [result, setResult] = useState(null);

    useEffect(() => { loadStations(); }, []);

    async function loadStations() {
        const [avRes, regRes] = await Promise.all([
            api.get("/admin/plants/available-stations").catch(() => ({ data: { stations: [], onboarded_count: 0, total_solaros_count: 0 } })),
            api.get("/admin/plants/registry").catch(() => ({ data: [] })),
        ]);
        // `stations` here is the "available" list (SolarOS − already-onboarded).
        setStations(avRes.data?.stations || []);
        setStationMeta({
            available: avRes.data?.available_count ?? 0,
            total: avRes.data?.total_solaros_count ?? 0,
            onboarded: avRes.data?.onboarded_count ?? 0,
        });
        setRegistry(regRes.data || []);
    }

    // Backend endpoint already returns only stations not yet onboarded — no
    // client-side filtering required.
    const availableStations = stations;

    // Currently selected SolarOS station (or null).
    const selectedStation = useMemo(
        () => stations.find(s => String(s.id) === String(form.solaros_station_id)),
        [stations, form.solaros_station_id]
    );

    // When a station is picked, pre-populate the editable Name + Location and
    // lock the Size to SolarOS's installedCapacity.
    function handleStationChange(newId) {
        const st = stations.find(s => String(s.id) === String(newId));
        setForm(f => ({
            ...f,
            solaros_station_id: newId,
            // Editable defaults — admin can rename before saving.
            customer_name: st?.solaros_name || st?.name || "",
            location: st?.address || st?.location || "",
            // Size is LOCKED to SolarOS. Never populate from the form.
            size_kw: st?.installed_capacity_kw != null ? String(st.installed_capacity_kw) : "",
        }));
    }

    // Client-side preview mirrors backend `build_plant_id`.
    const plantId = useMemo(() => {
        if (!form.customer_name || !form.size_kw || !form.location || !form.commissioning_date) return "";
        const slug = (v) => (v || "").trim().replace(/[^A-Za-z0-9]+/g, "-").replace(/^-|-$/g, "");
        const size = String(parseFloat(form.size_kw)).replace(/\.0+$/, "");
        const date = form.commissioning_date.replaceAll("-", "");
        return `${slug(form.customer_name)}_${size}_${slug(form.location)}_${date}`;
    }, [form.customer_name, form.size_kw, form.location, form.commissioning_date]);

    // Compute warranty end (commissioning + 5 years, IST semantics).
    const warrantyEnd = useMemo(() => {
        if (!form.commissioning_date) return "";
        const d = new Date(form.commissioning_date);
        if (Number.isNaN(d.getTime())) return "";
        d.setFullYear(d.getFullYear() + 5);
        return d.toISOString().slice(0, 10);
    }, [form.commissioning_date]);

    const existingIds = useMemo(() => new Set(registry.map(r => r.plant_id)), [registry]);
    const localConflict = plantId && existingIds.has(plantId);

    function update(k, v) { setForm(f => ({ ...f, [k]: v })); }

    async function submit(e) {
        e.preventDefault();
        if (!plantId || !form.solaros_station_id) return;
        setSubmitting(true);
        setResult(null);
        try {
            const payload = {
                customer_name: form.customer_name.trim(),
                customer_email: form.customer_email.trim(),
                customer_phone: form.customer_phone.trim(),
                size_kw: parseFloat(form.size_kw),
                location: form.location.trim(),
                commissioning_date: form.commissioning_date,
                solaros_station_id: parseInt(form.solaros_station_id, 10),
            };
            const { data } = await api.post("/admin/plants/register", payload);
            setResult({ ok: true, ...data });
            await loadStations();
            toast.success(`Plant "${data.plant.plant_id}" registered and welcome email sent`);
            setForm({
                solaros_station_id: "",
                customer_name: "",
                customer_email: "",
                customer_phone: "",
                size_kw: "",
                location: "",
                commissioning_date: new Date().toISOString().slice(0, 10),
            });
        } catch (e) {
            const detail = e?.response?.data?.detail || "Failed to register plant";
            setResult({ ok: false, error: detail });
            toast.error(detail);
        } finally { setSubmitting(false); }
    }

    async function handleDelete(entry) {
        if (!isSuperAdmin) return;
        const confirmed = window.confirm(
            `Delete "${entry.plant_id}"?\n\n` +
            `This removes the registry mapping, service tickets, alerts, ` +
            `documents and contact info for SolarOS station #${entry.solaros_station_id}. ` +
            `The customer's login will be detached from this plant.\n\n` +
            `This action cannot be undone.`
        );
        if (!confirmed) return;
        setDeletingId(entry.plant_id);
        try {
            await api.delete(`/admin/plants/registry/${encodeURIComponent(entry.plant_id)}`);
            toast.success(`Deleted "${entry.plant_id}"`);
            await loadStations();
        } catch (e) {
            const detail = e?.response?.data?.detail || "Delete failed";
            toast.error(detail);
        } finally { setDeletingId(null); }
    }

    return (
        <div className="space-y-6" data-testid="create-plant-page">
            <div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500">Administration</div>
                <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-zinc-900 mt-1">Onboard a plant</h1>
                <p className="text-sm text-[#6B7280] mt-1">
                    Pick a SolarOS station, verify the legal name and location, then mint a unique RPPL Plant ID and email the customer a secure welcome link.
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <form onSubmit={submit} className="bg-white border border-[#E5E7EB] rounded-md p-6 space-y-4" data-testid="create-plant-form">
                    {/* STEP 1 — Station selection FIRST */}
                    <div>
                        <label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500 mb-1.5 block">Step 1 · SolarOS Station</label>
                        <select
                            value={form.solaros_station_id}
                            onChange={e => handleStationChange(e.target.value)}
                            required
                            className="w-full h-9 px-3 text-sm rounded-sm border border-input bg-white focus-visible:ring-1 focus-visible:ring-[#0A0A0A] outline-none"
                            data-testid="create-plant-station"
                        >
                            <option value="">Select a SolarOS station…</option>
                            {availableStations.map(s => (
                                <option key={s.id} value={s.id}>
                                    {s.solaros_name || s.name} · #{s.id} · {s.installed_capacity_kw ?? "?"} kWp
                                </option>
                            ))}
                        </select>
                        <div className="text-[10px] text-zinc-500 mt-1" data-testid="station-availability-hint">
                            {stationMeta.total === 0
                                ? "Waiting for SolarOS sync…"
                                : availableStations.length === 0
                                    ? "Every SolarOS station is already onboarded."
                                    : `${stationMeta.available} of ${stationMeta.total} SolarOS stations available (${stationMeta.onboarded} already onboarded).`}
                        </div>
                    </div>

                    {/* Step 2+ — reveal editable fields once a station is picked */}
                    <fieldset disabled={!form.solaros_station_id} className={form.solaros_station_id ? "space-y-4 border-t border-[#F3F4F6] pt-4" : "space-y-4 border-t border-[#F3F4F6] pt-4 opacity-50 pointer-events-none"}>
                        <Field label="Step 2 · Legal Customer Name" testid="input-customer-name" hint="Editable — this overrides the SolarOS name everywhere.">
                            <Input value={form.customer_name} onChange={e => update("customer_name", e.target.value)} placeholder="e.g. Ravi Kumar" required className="rounded-sm" data-testid="create-plant-customer-name" />
                        </Field>
                        <Field label="Step 3 · Location" testid="input-location" hint="Editable — override the SolarOS address if needed.">
                            <Input value={form.location} onChange={e => update("location", e.target.value)} placeholder="e.g. MKB Nagar, Chennai" required className="rounded-sm" data-testid="create-plant-location" />
                        </Field>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <Field label="Commissioning Date" testid="input-date" hint="Drives the 5-year warranty period.">
                                <Input type="date" value={form.commissioning_date} onChange={e => update("commissioning_date", e.target.value)} required className="rounded-sm" data-testid="create-plant-date" max={new Date().toISOString().slice(0, 10)} />
                            </Field>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <Field label="Customer Email" testid="input-customer-email" hint="Where the secure welcome link is delivered.">
                                <Input type="email" value={form.customer_email} onChange={e => update("customer_email", e.target.value)} placeholder="customer@example.com" required className="rounded-sm" data-testid="create-plant-customer-email" />
                            </Field>
                            <Field label="Mobile Number" testid="input-customer-phone" hint="Used for WhatsApp alerts and support callbacks.">
                                <Input type="tel" value={form.customer_phone} onChange={e => update("customer_phone", e.target.value)} placeholder="+91 98765 43210" className="rounded-sm" data-testid="create-plant-customer-phone" />
                            </Field>
                        </div>
                    </fieldset>

                    <div className="border-t border-[#F3F4F6] pt-4 space-y-2" data-testid="plant-id-preview">
                        <div className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500">Preview</div>
                        <div className="flex items-center gap-2 flex-wrap">
                            <code className="font-mono text-sm px-2 py-1 rounded-sm bg-[#0B0D11] text-[#0A0A0A]" data-testid="plant-id-value">
                                {plantId || "—"}
                            </code>
                            {localConflict && (
                                <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-sm bg-red-50 text-red-700 border border-red-200" data-testid="plant-id-conflict">
                                    <AlertCircle className="w-3 h-3" /> Already in use — adjust the date
                                </span>
                            )}
                        </div>
                        {warrantyEnd && (
                            <div className="text-[11px] text-zinc-500" data-testid="warranty-preview">
                                Warranty: <span className="font-mono text-emerald-700">{form.commissioning_date}</span> &rarr; <span className="font-mono text-emerald-700">{warrantyEnd}</span> · 5 years
                            </div>
                        )}
                    </div>

                    <Button
                        type="submit"
                        disabled={submitting || !plantId || !form.solaros_station_id || localConflict}
                        className="w-full bg-[#0A0A0A] hover:bg-black text-white rounded-full h-10"
                        data-testid="create-plant-submit"
                    >
                        {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Registering…</> : <><Sparkles className="w-4 h-4 mr-2" /> Register & send welcome email</>}
                    </Button>
                </form>

                {/* Result / What happens next */}
                <div className="bg-white border border-[#E5E7EB] rounded-md p-6" data-testid="create-plant-result">
                    {!result ? (
                        <div className="text-sm text-zinc-500">
                            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500 mb-2">What happens next</div>
                            <ol className="list-decimal ml-5 space-y-1.5">
                                <li>We validate the Plant ID against the registry.</li>
                                <li>The SolarOS station <span className="font-mono">{form.solaros_station_id || "—"}</span> gets mapped to this customer.</li>
                                <li>Plant Size is copied straight from SolarOS &mdash; unchangeable.</li>
                                <li>A 5-year warranty is auto-derived from the commissioning date.</li>
                                <li>The Legal Name overrides the SolarOS name across dashboards and reports.</li>
                                <li>Resend delivers a 24-hour secure link so the customer sets their own password.</li>
                            </ol>
                        </div>
                    ) : result.ok ? (
                        <div className="space-y-3" data-testid="create-plant-success">
                            <div className="flex items-start gap-2 p-3 rounded-sm bg-green-50 border border-green-200 text-green-800 text-sm">
                                <CheckCircle2 className="w-4 h-4 mt-0.5" />
                                <div>
                                    <div className="font-semibold">Plant registered · Welcome email {result.email_status === "sent" ? "sent" : `queued (${result.email_status})`}</div>
                                    <div className="font-mono text-[11px] mt-1">{result.plant.plant_id}</div>
                                </div>
                            </div>
                            <ResultRow label="Customer" value={`${result.plant.customer_name} · ${result.plant.customer_email}${result.plant.customer_phone ? " · " + result.plant.customer_phone : ""}`} />
                            <ResultRow label="SolarOS Station" value={`#${result.plant.solaros_station_id}`} />
                            <ResultRow label="Commissioned" value={result.plant.commissioning_date} />
                            <ResultRow label="Warranty" value={`${result.plant.warranty_start} → ${result.plant.warranty_end} · 5 yrs`} />
                            <ResultRow label="Size" value={`${result.plant.size_kw} kWp`} />
                            {result.reset_token_debug && (
                                <div className="text-[10px] text-amber-700 font-mono bg-amber-50 border border-amber-200 rounded-sm p-2" data-testid="reset-token-debug">
                                    DEV · reset token (email not sent): {result.reset_token_debug}
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="p-3 rounded-sm bg-red-50 border border-red-200 text-red-800 text-sm flex items-start gap-2" data-testid="create-plant-error">
                            <AlertCircle className="w-4 h-4 mt-0.5" />
                            <div>
                                <div className="font-semibold">Registration failed</div>
                                <div className="text-xs mt-0.5">{result.error}</div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Onboarded Plants Registry */}
            <div className="bg-white border border-[#E5E7EB] rounded-md" data-testid="onboarded-registry">
                <div className="flex items-center justify-between px-6 py-4 border-b border-[#F3F4F6]">
                    <div>
                        <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500">Onboarded Plants Registry</div>
                        <div className="text-sm text-zinc-800 mt-0.5">{registry.length} plant{registry.length === 1 ? "" : "s"} currently under RPPL master data.</div>
                    </div>
                    {!isSuperAdmin && (
                        <div className="text-[10px] text-zinc-500 inline-flex items-center gap-1">
                            <ShieldCheck className="w-3 h-3" /> Delete restricted to Super Admins
                        </div>
                    )}
                </div>
                {registry.length === 0 ? (
                    <div className="p-8 text-center text-sm text-zinc-500" data-testid="registry-empty">
                        No plants have been onboarded yet. Start by picking a SolarOS station above.
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-[#F3F4F6] text-[10px] uppercase tracking-widest text-zinc-500">
                                    <th className="text-left px-6 py-3 font-semibold">Plant ID</th>
                                    <th className="text-left px-4 py-3 font-semibold">Customer</th>
                                    <th className="text-left px-4 py-3 font-semibold">Location</th>
                                    <th className="text-right px-4 py-3 font-semibold">Size</th>
                                    <th className="text-left px-4 py-3 font-semibold">Warranty</th>
                                    <th className="text-left px-4 py-3 font-semibold">SolarOS #</th>
                                    <th className="text-right px-6 py-3 font-semibold">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {registry.map(r => {
                                    const days = r.warranty_days_remaining;
                                    const tone = days == null ? "text-zinc-500" : days < 60 ? "text-red-600" : days < 365 ? "text-amber-600" : "text-emerald-700";
                                    return (
                                        <tr key={r.plant_id} className="border-b border-[#F3F4F6] hover:bg-zinc-50" data-testid={`registry-row-${r.plant_id}`}>
                                            <td className="px-6 py-3 font-mono text-[11px] text-[#0B0D11]">{r.plant_id}</td>
                                            <td className="px-4 py-3">
                                                <div className="font-medium text-zinc-800">{r.customer_name}</div>
                                                <div className="text-[10px] text-zinc-500">{r.customer_email}</div>
                                            </td>
                                            <td className="px-4 py-3 text-zinc-700">{r.location}</td>
                                            <td className="px-4 py-3 text-right font-mono text-zinc-700">{r.size_kw} kWp</td>
                                            <td className={`px-4 py-3 font-mono text-[11px] ${tone}`}>
                                                {r.warranty_end || "—"}
                                                {days != null && (
                                                    <div className="text-[10px] text-zinc-500 mt-0.5">
                                                        {days > 0 ? `${days} days left` : `expired ${Math.abs(days)}d ago`}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 text-zinc-600 font-mono text-[11px]">#{r.solaros_station_id}</td>
                                            <td className="px-6 py-3 text-right">
                                                {isSuperAdmin ? (
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => handleDelete(r)}
                                                        disabled={deletingId === r.plant_id}
                                                        className="h-7 px-2 rounded-sm border-red-200 text-red-600 hover:bg-red-50"
                                                        data-testid={`registry-delete-${r.plant_id}`}
                                                    >
                                                        {deletingId === r.plant_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                                                        <span className="ml-1 text-[11px]">Delete</span>
                                                    </Button>
                                                ) : (
                                                    <span className="text-[10px] text-zinc-400">—</span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

function Field({ label, children, testid, hint }) {
    return (
        <div data-testid={testid}>
            <label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500 mb-1.5 block">{label}</label>
            {children}
            {hint && <div className="text-[10px] text-zinc-500 mt-1">{hint}</div>}
        </div>
    );
}

function ResultRow({ label, value }) {
    return (
        <div className="flex items-center justify-between text-xs border-b border-[#F3F4F6] pb-1.5 last:border-b-0 last:pb-0">
            <span className="uppercase tracking-widest font-semibold text-zinc-500">{label}</span>
            <span className="font-mono text-zinc-800">{value}</span>
        </div>
    );
}
